import { lazy, Suspense, useMemo } from 'react';
import { useTranslation } from 'src/hooks/use-translation';

import { useNavigate } from 'src/hooks/use-navigate';
import { Box, Button, MenuItem, SelectProps, Skeleton } from '@mui/material';

import ArrowUpDownSvg from 'icons/monochrome/arrow-updown.svg';
import { CustomPopover, usePopover } from 'src/components/custom-popover';
import { Platform } from 'src/types';
import { useBoolean } from 'src/hooks/use-boolean';
import { PlatformItem } from 'src/layouts/dashboard/platform-popover/platform-item';
import { usePlatforms } from 'src/hooks/use-platforms';
import { useAuthContext } from 'src/auth/hooks';
import { PATHS } from 'src/routes/paths';
import { useFileLink } from 'src/hooks/use-file-link';
import { DEVELOPMENT } from 'src/config-global';
import { ErrorBoundary } from 'src/components/error-boundary/error-boundary';

const SUPPORT_TELEGRAM_LINK = 'https://t.me/menta_support';

const PlatformCreateModal = lazy(() =>
  import('src/components/platform-create-modal').then((module) => ({
    default: module.PlatformCreateModal,
  }))
);

export const PlatformPopover = ({ sx }: SelectProps) => {
  const popover = usePopover();
  const createModalVisible = useBoolean();
  const { value: isCreateModalLoaded, onTrue: loadCreateModal } = useBoolean();
  const { platformId } = useAuthContext();
  const navigate = useNavigate();

  const { data: platforms, isLoading } = usePlatforms();
  const getFileLink = useFileLink();
  const { t } = useTranslation('', { keyPrefix: 'platform-popover' });

  const activePlatform: Platform = useMemo(
    () => platforms.find((platform) => platform.id === platformId) || platforms?.[0],
    [platformId, platforms]
  );

  const handleActivePlatformChange = (platformId: Platform['id']) => {
    // Navigate to root with the new platform_id
    // This avoids race condition with setSearchParams
    navigate(`${PATHS.root}?platform_id=${platformId}`, { replace: true });
    popover.onClose();
  };

  const addButton = (
    <Button
      variant="contained"
      color="primary"
      sx={{ width: '100%', maxHeight: '50px', height: '100%' }}
      onClick={() => {
        if (DEVELOPMENT) {
          loadCreateModal();
          createModalVisible.onTrue();
        } else {
          window.open(SUPPORT_TELEGRAM_LINK, '_blank');
        }
        popover.onClose();
      }}
    >
      {t('create')}
    </Button>
  );

  if (isLoading) {
    return (
      <Skeleton
        width="200px"
        height="40px"
        animation="wave"
        sx={{ borderRadius: 1, width: '250px', height: '38px', ml: 2 }}
      />
    );
  }

  return (
    <>
      {platforms.length > 0 ? (
        <>
          <Button
            disableRipple
            data-testid="header-platform"
            onClick={popover.onOpen}
            sx={{
              backgroundColor: (theme) =>
                popover.open ? theme.palette.action.selected : 'inherit',
              ...sx,
            }}
          >
            <PlatformItem
              name={activePlatform.name}
              role={activePlatform.role}
              icon={getFileLink(activePlatform.images?.favicon)}
              adaptive
            />

            <Box
              component={ArrowUpDownSvg}
              width={16}
              height={16}
              sx={{ flexShrink: 0, color: (theme) => theme.palette.text.secondary, ml: 1 }}
            />
          </Button>

          <CustomPopover arrow="top-left" open={popover.open} onClose={popover.onClose}>
            <Box
              sx={{
                overflow: 'auto',
                maxHeight: '70vh',
              }}
            >
              {platforms.map((platform) => (
                <MenuItem
                  key={platform.id}
                  value={platform.id}
                  selected={platform.id === platformId}
                  aria-current={platform.id === platformId ? 'true' : undefined}
                  onClick={() => handleActivePlatformChange(platform.id)}
                  sx={{ py: 1.25, mb: '4px' }}
                >
                  <PlatformItem
                    name={platform.name}
                    role={platform.role}
                    icon={getFileLink(platform.images?.favicon, platform.bucket_name)}
                  />
                </MenuItem>
              ))}
            </Box>
            <Box sx={{ height: '50px' }}>{addButton}</Box>
          </CustomPopover>
        </>
      ) : (
        <Box sx={{ maxWidth: '250px', ml: 2 }}>{addButton}</Box>
      )}
      {isCreateModalLoaded && (
        <ErrorBoundary fallback={null}>
          <Suspense fallback={null}>
            <PlatformCreateModal
              open={createModalVisible.value}
              onClose={createModalVisible.onFalse}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};
